import {
  taskOutcomeFieldAppliesTo,
  type TaskOutcomeField,
} from '@erp/shared-types';

/**
 * Pure edits behind the per-outcome result editor. Storage stays one list of
 * fields; each field lists the outcomes it belongs to (`outcomes`, omitted =
 * every outcome) and the subset it is required for (`requiredFor`).
 */

/** camelCase machine key from a display label ("Approved amount" → approvedAmount). */
export function fieldKeyFromLabel(label: string): string {
  const words = label
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);
  const key = words
    .map((word, index) =>
      index === 0
        ? word.charAt(0).toLowerCase() + word.slice(1)
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join('')
    .replace(/^[^A-Za-z]+/, '');
  return key.slice(0, 64);
}

/**
 * Rename a field's label. A field added in this edit (`isNew`) keeps an
 * auto-derived key in step with it; saved keys never change behind the
 * admin's back, since stored results and workflows read them.
 */
export function relabelField(
  field: TaskOutcomeField,
  label: string,
  isNew: boolean,
): TaskOutcomeField {
  const keyFollowsLabel =
    isNew && (field.key === '' || field.key === fieldKeyFromLabel(field.label));
  return {
    ...field,
    label,
    ...(keyFollowsLabel ? { key: fieldKeyFromLabel(label) } : {}),
  };
}

export function newFieldForOutcome(outcome: string): TaskOutcomeField {
  return {
    key: '',
    label: '',
    type: 'text',
    outcomes: [outcome],
    requiredFor: [outcome],
  };
}

export function setFieldRequired(
  field: TaskOutcomeField,
  outcome: string,
  required: boolean,
): TaskOutcomeField {
  const rest = field.requiredFor.filter((value) => value !== outcome);
  return { ...field, requiredFor: required ? [...rest, outcome] : rest };
}

/**
 * Point a field at an explicit outcome list, in vocabulary order. Requirements
 * for outcomes it no longer belongs to are dropped. Returns null when the
 * field would belong to no outcome, so the caller deletes it.
 */
export function setFieldOutcomes(
  field: TaskOutcomeField,
  selected: readonly string[],
  vocabulary: readonly string[],
): TaskOutcomeField | null {
  const chosen = new Set(selected);
  const outcomes = vocabulary.filter((outcome) => chosen.has(outcome));
  if (outcomes.length === 0) return null;
  const kept = new Set(outcomes);
  return {
    ...field,
    outcomes,
    requiredFor: field.requiredFor.filter((outcome) => kept.has(outcome)),
  };
}

/** The outcomes a field currently belongs to, resolved against the vocabulary. */
export function fieldOutcomes(
  field: TaskOutcomeField,
  vocabulary: readonly string[],
): string[] {
  return vocabulary.filter((outcome) =>
    taskOutcomeFieldAppliesTo(field, outcome),
  );
}

export function toggleFieldOutcome(
  field: TaskOutcomeField,
  outcome: string,
  included: boolean,
  vocabulary: readonly string[],
): TaskOutcomeField | null {
  const current = fieldOutcomes(field, vocabulary).filter(
    (value) => value !== outcome,
  );
  return setFieldOutcomes(
    field,
    included ? [...current, outcome] : current,
    vocabulary,
  );
}

/** One line of the result contract preview. */
export type OutcomeResultEntry = {
  key: string;
  label: string;
  type: TaskOutcomeField['type'];
  required: boolean;
};

export function outcomeResultEntries(
  fields: readonly TaskOutcomeField[],
  outcome: string,
): OutcomeResultEntry[] {
  return fields.flatMap((field) =>
    !taskOutcomeFieldAppliesTo(field, outcome)
      ? []
      : [
          {
            key: field.key,
            label: field.label || field.key,
            type: field.type,
            required: field.requiredFor.includes(outcome),
          },
        ],
  );
}
